import { useCallback, useEffect, useRef } from "react";

const STORAGE_KEY = "civilfrontend_onboard_state";

/**
 * A custom hook that persists wizard state to localStorage so it survives page refreshes.
 * 
 * Usage:
 *   const [getPersisted, setPersisted, clearPersisted] = useLocalStoragePersist();
 *   
 *   // On mount, restore state:
 *   const saved = getPersisted();
 *   if (saved) { setState(saved); }
 *   
 *   // On state changes, persist:
 *   setPersisted({ key: value, ... });
 *   
 *   // On completion/cancel:
 *   clearPersisted();
 */
export default function useLocalStoragePersist() {
    const storageRef = useRef(null);

    /**
     * Load persisted state from localStorage.
     * Returns null if no valid state is found.
     * Optional projectId parameter validates that cached state matches the requested project.
     */
    const getPersisted = useCallback((projectId = null) => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            // Validate that it looks like our state
            if (!parsed || typeof parsed !== "object") {
                localStorage.removeItem(STORAGE_KEY);
                return null;
            }
            
            // If projectId is provided, validate it matches the cached state
            // This prevents using stale localStorage data for a different project
            if (projectId && parsed.projectId && parsed.projectId !== projectId) {
                console.log(`localStorage projectId mismatch: cached=${parsed.projectId}, requested=${projectId}. Ignoring cached state.`);
                return null;
            }
            
            return parsed;
        } catch (e) {
            // Corrupted data – clean up
            try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
            return null;
        }
    }, []);

    /**
     * Save state to localStorage.
     * Only stores serializable keys (filters out File objects).
     */
    const setPersisted = useCallback((state) => {
        try {
            // Strip non-serializable data (File objects, Blobs, URLs)
            const serializable = JSON.parse(JSON.stringify(state, (key, value) => {
                // Skip File objects and functions
                if (typeof value === "function") return undefined;
                if (value instanceof File) return undefined;
                if (value instanceof Blob) return undefined;
                // Keep object URLs as strings for display only
                if (key === "url" && typeof value === "string" && value.startsWith("blob:")) {
                    return value; // Keep blob URLs for display (they won't work after refresh but that's okay)
                }
                return value;
            }));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
        } catch (e) {
            console.warn("Failed to persist wizard state:", e);
        }
    }, []);

    /**
     * Clear persisted state from localStorage.
     */
    const clearPersisted = useCallback(() => {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            console.warn("Failed to clear persisted state:", e);
        }
    }, []);

    return [getPersisted, setPersisted, clearPersisted];
}